// src/components/resources/tools/generators/EmailThread/OutlookPreview.tsx

'use client'

import { useRef } from 'react'
import {
  Archive, Star, Search, Inbox, Send, File, Trash2, Mail, Edit3, Paperclip, Image as ImageIcon, Link2, Tag
} from 'lucide-react'
import { clsx } from 'clsx'
import { OutlookMessageRow } from './OutlookMessageRow'
import type { EmailConfigChange, EmailThreadConfig } from './types'

/**
 * Outlook-style thread preview component.
 */
export function OutlookPreview({
  config,
  onConfigChange,
}: {
  config: EmailThreadConfig
  onConfigChange: EmailConfigChange
}): JSX.Element {
  const threadRef = useRef<HTMLDivElement>(null)

  const handleToggleThreadStar = (): void => {
    onConfigChange(prev => ({ starred: !prev.starred }))
  }

  const handleToggleArchived = (): void => {
    onConfigChange(prev => ({ archived: !prev.archived }))
  }

  return (
    <div
      ref={threadRef}
      id="email-thread-preview"
      className="flex h-full w-full flex-col bg-white dark:bg-slate-900"
      style={{ borderRadius: 18, boxShadow: '0 2px 16px 0 rgba(0,0,0,0.06)' }}
    >
      {/* Top Ribbon */}
      <div className="border-b border-gray-200 dark:border-gray-700" style={{ backgroundColor: '#0078d4' }}>
        <div className="flex items-center gap-4 px-4 py-2">
          <div className="flex items-center gap-2">
            <div className="grid grid-cols-3 gap-0.5 p-2" aria-hidden="true">
              {Array.from({ length: 9 }).map((_, i): JSX.Element => (
                <div key={i} className="h-1 w-1 rounded-full bg-white"></div>
              ))}
            </div>
            <span className="text-sm font-semibold text-white">Outlook</span>
          </div>
          <div className="flex flex-1 items-center gap-2 bg-white/20 rounded px-3 py-1">
            <Search className="h-4 w-4 text-white" />
            <input type="text" placeholder="Search" className="flex-1 bg-transparent text-sm text-white placeholder-white/70 outline-none" aria-label="Search" />
          </div>
          <div className="h-8 w-8 rounded-full bg-white/20 flex items-center justify-center text-white text-xs font-medium" aria-label="User avatar">
            ME
          </div>
        </div>
        <div className="flex items-center gap-6 px-4 py-1 text-xs text-white">
          <button className="py-2 border-b-2 border-white font-medium" aria-current="page">Home</button>
          <button className="py-2 hover:bg-white/10">View</button>
          <button className="py-2 hover:bg-white/10">Help</button>
        </div>
      </div>

      {/* Command Bar */}
      <div className="flex items-center gap-2 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-2">
        <button
          className={clsx(
            'flex items-center gap-1 rounded border px-3 py-1 text-xs',
            config.archived
              ? 'bg-green-50 dark:bg-green-900/30 border-green-200 dark:border-green-700'
              : 'bg-white dark:bg-slate-800 border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
          )}
          onClick={handleToggleArchived}
          aria-label={config.archived ? 'Unarchive thread' : 'Archive thread'}
        >
          <Archive className={clsx(
            'h-3.5 w-3.5',
            config.archived ? 'text-green-600 dark:text-green-400' : 'text-gray-700 dark:text-gray-300'
          )} />
          <span className="text-gray-700 dark:text-gray-300">Archive</span>
        </button>
        <button className="flex items-center gap-1 rounded bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 px-3 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300" aria-label="Delete">
          <Trash2 className="h-3.5 w-3.5" />
          Delete
        </button>
        <button className="flex items-center gap-1 rounded bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 px-3 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300" aria-label="Mark as read">
          <Mail className="h-3.5 w-3.5" />
          Mark as read
        </button>
        <div className="h-4 w-px bg-gray-300 dark:bg-gray-600" aria-hidden="true" />
        <button className="flex items-center gap-1 rounded bg-white dark:bg-slate-800 border border-gray-300 dark:border-gray-600 px-3 py-1 text-xs hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300" aria-label="Categorize">
          <Tag className="h-3.5 w-3.5" />
          Categorize
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Folder Pane */}
        <aside className="hidden sm:block w-56 flex-shrink-0 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
          <button className="w-full flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2.5 text-sm font-medium m-2 rounded">
            <Edit3 className="h-4 w-4" />
            New mail
          </button>

          <nav className="px-2 space-y-1" aria-label="Sidebar folders">
            <div className="py-2 px-2 text-xs font-semibold text-gray-600 dark:text-gray-400">Folders</div>
            <button className="flex w-full items-center gap-2 rounded bg-blue-50 dark:bg-blue-900/30 px-3 py-1.5 text-sm text-gray-900 dark:text-gray-100">
              <Inbox className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="flex-1 text-left">Inbox</span>
              <span className="text-xs font-semibold text-blue-600 dark:text-blue-400">3</span>
            </button>
            <button className="flex w-full items-center gap-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">
              <File className="h-4 w-4" />
              <span className="flex-1 text-left">Drafts</span>
            </button>
            <button className="flex w-full items-center gap-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">
              <Send className="h-4 w-4" />
              <span className="flex-1 text-left">Sent Items</span>
            </button>
            <button className="flex w-full items-center gap-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">
              <Trash2 className="h-4 w-4" />
              <span className="flex-1 text-left">Deleted Items</span>
            </button>
            <button className="flex w-full items-center gap-2 rounded hover:bg-gray-100 dark:hover:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-300">
              <Archive className="h-4 w-4" />
              <span className="flex-1 text-left">Archive</span>
            </button>
          </nav>
        </aside>

        {/* Reading Pane */}
        <div className="flex flex-1 flex-col overflow-hidden">
          {/* Message Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-start justify-between">
              <div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">{config.subject}</h2>
                <div className="mt-1 flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                  <Inbox className="h-4 w-4" />
                  <span>Inbox</span>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button
                  className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded transition-colors"
                  onClick={handleToggleThreadStar}
                  aria-label="Star thread"
                >
                  <Star className={clsx(
                    'h-4 w-4',
                    config.starred ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400 dark:text-gray-500'
                  )} />
                </button>
              </div>
            </div>
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto px-6 py-4" id="email-thread-inner-preview">
            <div className="space-y-6">
              {config.messages.map((message, idx) => (
                <OutlookMessageRow
                  key={message.id}
                  message={message}
                  isLast={idx === config.messages.length - 1}
                  showAvatars={config.showAvatars !== false}
                  highlightReply={Boolean(config.highlightReply)}
                />
              ))}
            </div>

            {/* Reply Section */}
            <div className="mt-6 border-t border-gray-200 dark:border-gray-700 pt-4">
              <div className="flex items-center gap-2 mb-3">
                <button className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded text-sm font-medium" aria-label="Reply">
                  Reply
                </button>
                <button className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded text-sm" aria-label="Reply all">
                  Reply all
                </button>
                <button className="flex items-center gap-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 px-4 py-2 rounded text-sm" aria-label="Forward">
                  Forward
                </button>
              </div>

              <div className="border border-gray-300 dark:border-gray-600 rounded">
                <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-3 py-2 flex items-center gap-2">
                  <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" aria-label="Attach file">
                    <Paperclip className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" aria-label="Insert image">
                    <ImageIcon className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                  <button className="p-1 hover:bg-gray-200 dark:hover:bg-gray-700 rounded" aria-label="Insert link">
                    <Link2 className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                  </button>
                </div>
                <textarea
                  className="w-full px-4 py-3 text-sm outline-none resize-none bg-white dark:bg-slate-900 text-gray-900 dark:text-gray-100"
                  rows={5}
                  placeholder="Type your reply..."
                  aria-label="Reply message"
                />
                <div className="border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-4 py-2 flex items-center justify-between">
                  <div className="text-xs text-gray-500 dark:text-gray-400">Press Ctrl+Enter to send</div>
                  <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-1.5 rounded text-sm font-medium" aria-label="Send reply">
                    Send
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
